import {
  getLabelFromMarkup,
  MEASUREMENT_REPLICATION_ORIGIN,
  MeasurementsCapabilityProvider,
  pointPositionFormatter,
  PointMeasurementType,
  Web3dTool,
} from '@trimble/web3d-providers';
import { SimpleMeasurementCapabilityBase } from './base-simple-measurement-capability';
import { createEffect, on } from 'solid-js';
import { detectArrayChanges } from '@trimble/utilities';
import { Web3DViewer } from '@technology/web3d';
import { Markup, PointMarkup } from '@technology/web3d-plugin-markup';

export class PointMeasurementCapability extends SimpleMeasurementCapabilityBase {
  protected measurementType: Web3dTool = Web3dTool.PointMeasurement;

  constructor(measurementsProvider: MeasurementsCapabilityProvider) {
    super(measurementsProvider);

    createEffect(
      on(this.viewerWrappers, (viewerWrappers, previousViewerWrappers) => {
        const {
          newItems: newViewerWrappers,
          removedItems: removedViewerWrappers,
        } = detectArrayChanges(previousViewerWrappers, viewerWrappers);

        // Remove event listeners from removed viewers
        removedViewerWrappers.forEach((vw) => {
          this.removeAllViewerEventListeners(vw.viewer);
        });

        if (!newViewerWrappers.length) {
          return;
        }

        for (const vw of newViewerWrappers) {
          this.addEventListenersToViewer(vw.viewer);
        }
      })
    );
  }

  private addEventListenersToViewer(viewer: Web3DViewer): void {
    const markupAddedHandler = this.handleMarkupAdded(viewer);
    this.addViewerEventListener(viewer, 'markupadded', markupAddedHandler);
  }

  private findPointMeasurementForMarkup(
    markupId: number
  ): PointMeasurementType | undefined {
    const measurement = this.measurementsProvider
      .items()
      .find(
        (item): item is PointMeasurementType =>
          item.type === 'point' &&
          item.markups.some((existing) => existing?.id === markupId)
      );
    return measurement;
  }

  private readonly handleMarkupAdded =
    (viewer: Web3DViewer) =>
    (e: Event): void => {
      queueMicrotask(() => {
        const markup = (e as CustomEvent<Markup>).detail;
        if (!markup || markup.origin === MEASUREMENT_REPLICATION_ORIGIN) {
          return;
        }

        if (markup.name !== 'pointMarkup') {
          return;
        }

        const existingMeasurement = this.findPointMeasurementForMarkup(
          markup.id
        );
        const items = this.measurementsProvider.items();
        const pointSettings =
          this.measurementsProvider.pointMeasurementSettings();
        const label =
          existingMeasurement?.label ?? getLabelFromMarkup(markup, items);
        const showXYCoordinates =
          existingMeasurement?.showXYCoordinates ??
          pointSettings.showXYCoordinates;
        const showZCoordinate =
          existingMeasurement?.showZCoordinate ?? pointSettings.showZCoordinate;

        (markup as PointMarkup).positionFormatter = (position) =>
          pointPositionFormatter(
            position,
            viewer,
            showXYCoordinates,
            showZCoordinate,
            label
          );
      });
    };
}
